import React, { useState, useEffect, useMemo, useCallback } from 'react';

// ============================================
// CONFIGURATION - Hardcoded credentials
// ============================================
const CONFIG = {
  API_KEY: 'patpAQlEnWDMz9CVb.613396a3eae9383de803bd8707aa2a3108df366f26daee6ec7319360b4595e06',
  BASE_ID: 'appr56SwuRemZvsOb',
  TABLES: {
    FAMILY_MEMBERS: 'tblJoGRVJv48ljWo3',
    FOOD_LOG: 'tblsTB5lMy6j8QjUh',
    ACTIVITY_LOG: 'tblJCx9velchCZInA',
    DAILY_SUMMARY: 'tbldIPdS823IMLCej',
  },
};

// ============================================
// AIRTABLE API SERVICE
// ============================================
class AirtableService {
  constructor() {
    this.apiKey = CONFIG.API_KEY;
    this.baseId = CONFIG.BASE_ID;
    this.baseUrl = `https://api.airtable.com/v0/${this.baseId}`;
  }

  async fetchTable(tableId, options = {}) {
    const params = new URLSearchParams();
    
    if (options.filterByFormula) {
      params.append('filterByFormula', options.filterByFormula);
    }
    if (options.sort) {
      options.sort.forEach((s, i) => {
        params.append(`sort[${i}][field]`, s.field);
        params.append(`sort[${i}][direction]`, s.direction || 'asc');
      });
    }
    if (options.fields) {
      options.fields.forEach(f => params.append('fields[]', f));
    }
    if (options.maxRecords) {
      params.append('maxRecords', options.maxRecords);
    }

    const url = `${this.baseUrl}/${tableId}?${params.toString()}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch from Airtable');
    }

    const data = await response.json();
    return data.records;
  }

  async fetchAllPages(tableId, options = {}) {
    let allRecords = [];
    let offset = null;

    do {
      const params = new URLSearchParams();
      
      if (options.filterByFormula) {
        params.append('filterByFormula', options.filterByFormula);
      }
      if (options.sort) {
        options.sort.forEach((s, i) => {
          params.append(`sort[${i}][field]`, s.field);
          params.append(`sort[${i}][direction]`, s.direction || 'asc');
        });
      }
      if (offset) {
        params.append('offset', offset);
      }

      const url = `${this.baseUrl}/${tableId}?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to fetch from Airtable');
      }

      const data = await response.json();
      allRecords = [...allRecords, ...data.records];
      offset = data.offset;
    } while (offset);

    return allRecords;
  }

  async getFamilyMembers() {
    const records = await this.fetchTable(CONFIG.TABLES.FAMILY_MEMBERS, {
      filterByFormula: '{Active} = TRUE()',
      sort: [{ field: 'Name', direction: 'asc' }],
    });

    return records.map((record, index) => ({
      id: record.id,
      name: record.fields.Name || 'Unknown',
      initials: record.fields.Initials || record.fields.Name?.[0] || '?',
      color: record.fields.Color || ['#2C3E50', '#6B4C5A', '#4A6741', '#8B6914'][index % 4],
    }));
  }

  async getFoodLog(startDate, endDate) {
    // Add one day to endDate because Airtable's <= comparison
    // doesn't reliably include the boundary date
    const endDateObj = new Date(endDate + 'T00:00:00');
    endDateObj.setDate(endDateObj.getDate() + 1);
    const endDatePlusOne = `${endDateObj.getFullYear()}-${String(endDateObj.getMonth() + 1).padStart(2, '0')}-${String(endDateObj.getDate()).padStart(2, '0')}`;
    
    const formula = `AND(
      {Date} >= '${startDate}',
      {Date} < '${endDatePlusOne}'
    )`;

    const records = await this.fetchAllPages(CONFIG.TABLES.FOOD_LOG, {
      filterByFormula: formula,
      sort: [{ field: 'Date', direction: 'asc' }],
    });

    return records.map(record => ({
      id: record.id,
      name: record.fields['Item Name'] || 'Unknown Item',
      personId: record.fields.Person?.[0] || null,
      date: record.fields.Date,
      mealType: record.fields['Meal Type'] || 'Snack',
      sugar: record.fields['Processed Sugar'] || false,
      carbs: record.fields['Heavy Carbs'] || false,
      notes: record.fields.Notes || '',
    }));
  }

  async getActivityLog(startDate, endDate) {
    // Add one day to endDate because Airtable's <= comparison
    // doesn't reliably include the boundary date
    const endDateObj = new Date(endDate + 'T00:00:00');
    endDateObj.setDate(endDateObj.getDate() + 1);
    const endDatePlusOne = `${endDateObj.getFullYear()}-${String(endDateObj.getMonth() + 1).padStart(2, '0')}-${String(endDateObj.getDate()).padStart(2, '0')}`;
    
    const formula = `AND(
      {Date} >= '${startDate}',
      {Date} < '${endDatePlusOne}'
    )`;

    const records = await this.fetchAllPages(CONFIG.TABLES.ACTIVITY_LOG, {
      filterByFormula: formula,
      sort: [{ field: 'Date', direction: 'asc' }],
    });

    return records.map(record => ({
      id: record.id,
      name: record.fields['Activity Name'] || 'Activity',
      personId: record.fields.Person?.[0] || null,
      date: record.fields.Date,
      duration: record.fields.Duration || `${record.fields['Duration Minutes'] || 0} min`,
      durationMinutes: record.fields['Duration Minutes'] || 0,
      type: record.fields['Activity Type'] || 'Other',
      notes: record.fields.Notes || '',
    }));
  }

  async createFoodEntry(entry) {
    const url = `${this.baseUrl}/${CONFIG.TABLES.FOOD_LOG}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        records: [{
          fields: {
            'Item Name': entry.name,
            'Person': [entry.personId],
            'Date': entry.date,
            'Meal Type': entry.mealType,
            'Processed Sugar': entry.sugar || false,
            'Heavy Carbs': entry.carbs || false,
            'Notes': entry.notes || '',
          },
        }],
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to create entry');
    }

    return response.json();
  }

  // Batch create multiple food entries at once
  async createFoodEntries(entries) {
    if (!entries || entries.length === 0) return;
    
    const url = `${this.baseUrl}/${CONFIG.TABLES.FOOD_LOG}`;
    
    // Airtable allows up to 10 records per request
    const batches = [];
    for (let i = 0; i < entries.length; i += 10) {
      batches.push(entries.slice(i, i + 10));
    }
    
    for (const batch of batches) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          records: batch.map(entry => ({
            fields: {
              'Item Name': entry.name,
              'Person': [entry.personId],
              'Date': entry.date,
              'Meal Type': entry.mealType,
              'Processed Sugar': entry.sugar || false,
              'Heavy Carbs': entry.carbs || false,
              'Notes': entry.notes || '',
            },
          })),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to create entries');
      }
    }
  }

  async updateFoodEntry(recordId, entry) {
    const url = `${this.baseUrl}/${CONFIG.TABLES.FOOD_LOG}/${recordId}`;
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fields: {
          'Item Name': entry.name,
          'Person': [entry.personId],
          'Date': entry.date,
          'Meal Type': entry.mealType,
          'Processed Sugar': entry.sugar || false,
          'Heavy Carbs': entry.carbs || false,
          'Notes': entry.notes || '',
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to update entry');
    }

    return response.json();
  }

  async deleteFoodEntry(recordId) {
    const url = `${this.baseUrl}/${CONFIG.TABLES.FOOD_LOG}/${recordId}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to delete entry');
    }

    return response.json();
  }

  async createActivityEntry(entry) {
    const url = `${this.baseUrl}/${CONFIG.TABLES.ACTIVITY_LOG}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        records: [{
          fields: {
            'Activity Name': entry.name,
            'Person': [entry.personId],
            'Date': entry.date,
            'Duration': entry.duration,
            'Duration Minutes': entry.durationMinutes || 0,
            'Activity Type': entry.type || 'Other',
            'Notes': entry.notes || '',
          },
        }],
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to create entry');
    }

    return response.json();
  }

  async updateActivityEntry(recordId, entry) {
    const url = `${this.baseUrl}/${CONFIG.TABLES.ACTIVITY_LOG}/${recordId}`;
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fields: {
          'Activity Name': entry.name,
          'Person': [entry.personId],
          'Date': entry.date,
          'Duration': entry.duration,
          'Duration Minutes': entry.durationMinutes || 0,
          'Activity Type': entry.type || 'Other',
          'Notes': entry.notes || '',
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to update entry');
    }

    return response.json();
  }

  async deleteActivityEntry(recordId) {
    const url = `${this.baseUrl}/${CONFIG.TABLES.ACTIVITY_LOG}/${recordId}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to delete entry');
    }

    return response.json();
  }
}

// Create singleton instance
const airtable = new AirtableService();

// ============================================
// DATE UTILITIES
// ============================================
const formatDateLocal = (date) => {
  // Format date as YYYY-MM-DD in local timezone (not UTC)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getWeekDates = (referenceDate = new Date()) => {
  const date = new Date(referenceDate);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  
  const monday = new Date(date.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  
  const weekDays = [];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const shortNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  for (let i = 0; i < 7; i++) {
    const currentDay = new Date(monday);
    currentDay.setDate(monday.getDate() + i);
    
    weekDays.push({
      name: dayNames[i],
      short: shortNames[i],
      date: currentDay.getDate().toString(),
      fullDate: formatDateLocal(currentDay),
      dateObj: currentDay,
    });
  }
  
  return weekDays;
};

const formatWeekRange = (weekDays) => {
  if (!weekDays.length) return '';
  
  const startDate = weekDays[0].dateObj;
  const endDate = weekDays[6].dateObj;
  
  const startMonth = startDate.toLocaleDateString('en-US', { month: 'long' });
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'long' });
  const year = startDate.getFullYear();
  
  if (startMonth === endMonth) {
    return `${startMonth} ${startDate.getDate()} – ${endDate.getDate()}, ${year}`;
  }
  return `${startMonth} ${startDate.getDate()} – ${endMonth} ${endDate.getDate()}, ${year}`;
};

// ============================================
// DATA TRANSFORMATION
// ============================================
const transformDataForWeek = (familyMembers, foodLog, activityLog, weekDays) => {
  const data = {};
  
  familyMembers.forEach(member => {
    data[member.id] = {};
    weekDays.forEach(day => {
      data[member.id][day.name] = {
        Breakfast: [],
        Lunch: [],
        Dinner: [],
        Snacks: [],
        Activity: [],
      };
    });
  });
  
  const mealTypeMap = {
    'Breakfast': 'Breakfast',
    'Lunch': 'Lunch',
    'Dinner': 'Dinner',
    'Snack': 'Snacks',
  };
  
  foodLog.forEach(item => {
    if (!item.personId || !data[item.personId]) return;
    
    const dayInfo = weekDays.find(d => d.fullDate === item.date);
    if (!dayInfo) return;
    
    const mealType = mealTypeMap[item.mealType] || 'Snacks';
    
    data[item.personId][dayInfo.name][mealType].push({
      id: item.id,
      name: item.name,
      sugar: item.sugar,
      carbs: item.carbs,
      personId: item.personId,
      date: item.date,
      mealType: item.mealType,
      type: 'food',
    });
  });
  
  activityLog.forEach(activity => {
    if (!activity.personId || !data[activity.personId]) return;
    
    const dayInfo = weekDays.find(d => d.fullDate === activity.date);
    if (!dayInfo) return;
    
    data[activity.personId][dayInfo.name].Activity.push({
      id: activity.id,
      name: activity.name,
      duration: activity.duration,
      durationMinutes: activity.durationMinutes,
      activityType: activity.type,
      personId: activity.personId,
      date: activity.date,
      type: 'activity',
    });
  });
  
  return data;
};

// ============================================
// SHARED UI COMPONENTS
// ============================================
const mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snacks'];
const mealLabels = { Breakfast: 'B', Lunch: 'L', Dinner: 'D', Snacks: 'S' };
// Map display names back to Airtable values
const mealTypeToAirtable = { Breakfast: 'Breakfast', Lunch: 'Lunch', Dinner: 'Dinner', Snacks: 'Snack' };

const getItemStyle = (item, isClickable = false) => {
  let baseStyle = '';
  if (item.sugar && item.carbs) {
    baseStyle = 'bg-gradient-to-r from-rose-100 to-amber-100 text-gray-800 px-1 rounded';
  } else if (item.sugar) {
    baseStyle = 'bg-rose-100 text-rose-800 px-1 rounded';
  } else if (item.carbs) {
    baseStyle = 'bg-amber-100 text-amber-800 px-1 rounded';
  }
  
  if (isClickable) {
    baseStyle += ' cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all';
  }
  
  return baseStyle;
};

// Loading skeleton
const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="h-24 bg-gray-100 rounded"></div>
      ))}
    </div>
  </div>
);

// Error display
const ErrorDisplay = ({ error, onRetry }) => (
  <div 
    className="min-h-screen flex items-center justify-center p-6"
    style={{ backgroundColor: '#FAF9F6', fontFamily: 'Georgia, "Times New Roman", serif' }}
  >
    <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center" style={{ border: '1px solid #E5E2DC' }}>
      <div className="text-4xl mb-4">⚠️</div>
      <h2 className="text-xl font-normal mb-2" style={{ color: '#2C3E50' }}>
        Connection Error
      </h2>
      <p className="text-sm text-gray-500 mb-6" style={{ fontFamily: 'system-ui, sans-serif' }}>
        {error}
      </p>
      <button
        onClick={onRetry}
        className="px-6 py-2 rounded text-white font-medium transition-colors hover:opacity-90"
        style={{ backgroundColor: '#2C3E50' }}
      >
        Try Again
      </button>
    </div>
  </div>
);

// ============================================
// MULTI-ITEM ENTRY MODAL
// ============================================
const EntryModal = ({ 
  isOpen, 
  onClose, 
  familyMembers, 
  weekDays, 
  selectedDayIndex, 
  onAddFood, 
  onAddFoods,
  onAddActivity,
  onUpdateFood,
  onUpdateActivity,
  onDeleteFood,
  onDeleteActivity,
  editItem, // null for add, object for edit
  defaultMealType,
  defaultPersonId,
  defaultDate,
  defaultIsActivity,
  onMealTypeChange,
}) => {
  const isEditing = !!editItem;
  const [entryType, setEntryType] = useState(editItem?.type || 'food');
  const [personId, setPersonId] = useState(editItem?.personId || defaultPersonId || '');
  const [date, setDate] = useState(editItem?.date || defaultDate || '');
  const [mealType, setMealType] = useState(editItem?.mealType || defaultMealType || 'Breakfast');
  
  // Multi-item state for adding new food entries
  const [foodItems, setFoodItems] = useState([{ id: 1, name: '', sugar: false, carbs: false, isDrink: false }]);
  const [nextItemId, setNextItemId] = useState(2);
  
  // Single item state for editing
  const [name, setName] = useState(editItem?.name || '');
  const [hasSugar, setHasSugar] = useState(editItem?.sugar || false);
  const [hasCarbs, setHasCarbs] = useState(editItem?.carbs || false);
  const [isDrink, setIsDrink] = useState(editItem?.isDrink || false);
  
  // Additional items when editing (to add more to same meal)
  const [additionalItems, setAdditionalItems] = useState([]);
  const [nextAdditionalId, setNextAdditionalId] = useState(1);
  
  // Activity fields (single item for editing)
  const [duration, setDuration] = useState(editItem?.duration || '');
  const [activityType, setActivityType] = useState(editItem?.activityType || 'Other');
  
  // Multi-item state for adding new activities
  const [activityItems, setActivityItems] = useState([{ id: 1, type: 'Cardio', duration: '' }]);
  const [nextActivityId, setNextActivityId] = useState(2);
  
  // Additional activities when editing
  const [additionalActivities, setAdditionalActivities] = useState([]);
  const [nextAdditionalActivityId, setNextAdditionalActivityId] = useState(1);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Reset form when modal opens/closes or editItem changes
  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        // Editing mode - single item
        setEntryType(editItem.type || 'food');
        setPersonId(editItem.personId || '');
        setDate(editItem.date || '');
        setName(editItem.name || '');
        setMealType(editItem.mealType || 'Breakfast');
        setHasSugar(editItem.sugar || false);
        setHasCarbs(editItem.carbs || false);
        setIsDrink(editItem.isDrink || false);
        setDuration(editItem.duration || '');
        setActivityType(editItem.activityType || 'Other');
        setAdditionalItems([]);
        setNextAdditionalId(1);
        setAdditionalActivities([]);
        setNextAdditionalActivityId(1);
      } else {
        // Adding mode - check if activity or food
        setEntryType(defaultIsActivity ? 'activity' : 'food');
        setFoodItems([{ id: 1, name: '', sugar: false, carbs: false, isDrink: false }]);
        setNextItemId(2);
        setName('');
        setHasSugar(false);
        setHasCarbs(false);
        setIsDrink(false);
        setDuration('');
        setActivityType('Other');
        setAdditionalItems([]);
        setNextAdditionalId(1);
        setActivityItems([{ id: 1, type: 'Cardio', duration: '' }]);
        setNextActivityId(2);
        setAdditionalActivities([]);
        setNextAdditionalActivityId(1);
        
        // Use defaults
        if (defaultPersonId) {
          setPersonId(defaultPersonId);
        } else if (familyMembers.length) {
          setPersonId(familyMembers[0].id);
        }
        
        if (defaultDate) {
          setDate(defaultDate);
        } else if (weekDays.length) {
          setDate(weekDays[selectedDayIndex]?.fullDate || weekDays[0].fullDate);
        }
        
        if (defaultMealType) {
          setMealType(defaultMealType);
        }
      }
      setShowDeleteConfirm(false);
    }
  }, [isOpen, editItem, familyMembers, weekDays, selectedDayIndex, defaultMealType, defaultPersonId, defaultDate, defaultIsActivity]);
  
  // Update when defaults change while modal is open (for new entries)
  useEffect(() => {
    if (!isEditing && isOpen) {
      if (defaultPersonId && !personId) {
        setPersonId(defaultPersonId);
      } else if (familyMembers.length && !personId) {
        setPersonId(familyMembers[0].id);
      }
      if (defaultDate && !date) {
        setDate(defaultDate);
      } else if (weekDays.length && !date) {
        setDate(weekDays[selectedDayIndex]?.fullDate || weekDays[0].fullDate);
      }
    }
  }, [familyMembers, weekDays, selectedDayIndex, isEditing, isOpen, personId, date, defaultPersonId, defaultDate]);
  
  // Notify parent when meal type changes
  const handleMealTypeChange = (newMealType) => {
    setMealType(newMealType);
    if (onMealTypeChange) {
      onMealTypeChange(newMealType);
    }
  };
  
  // Multi-item handlers
  const addFoodItem = () => {
    setFoodItems([...foodItems, { id: nextItemId, name: '', sugar: false, carbs: false, isDrink: false }]);
    setNextItemId(nextItemId + 1);
  };
  
  const removeFoodItem = (id) => {
    if (foodItems.length > 1) {
      setFoodItems(foodItems.filter(item => item.id !== id));
    }
  };
  
  const updateFoodItem = (id, field, value) => {
    setFoodItems(foodItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };
  
  // Additional items handlers (for edit mode)
  const addAdditionalItem = () => {
    setAdditionalItems([...additionalItems, { id: nextAdditionalId, name: '', sugar: false, carbs: false, isDrink: false }]);
    setNextAdditionalId(nextAdditionalId + 1);
  };
  
  const removeAdditionalItem = (id) => {
    setAdditionalItems(additionalItems.filter(item => item.id !== id));
  };
  
  const updateAdditionalItem = (id, field, value) => {
    setAdditionalItems(additionalItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };
  
  // Activity items handlers (for multi-add)
  const addActivityItem = () => {
    setActivityItems([...activityItems, { id: nextActivityId, type: 'Cardio', duration: '' }]);
    setNextActivityId(nextActivityId + 1);
  };
  
  const removeActivityItem = (id) => {
    if (activityItems.length > 1) {
      setActivityItems(activityItems.filter(item => item.id !== id));
    }
  };
  
  const updateActivityItem = (id, field, value) => {
    setActivityItems(activityItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };
  
  // Additional activities handlers (for edit mode)
  const addAdditionalActivity = () => {
    setAdditionalActivities([...additionalActivities, { id: nextAdditionalActivityId, type: 'Cardio', duration: '' }]);
    setNextAdditionalActivityId(nextAdditionalActivityId + 1);
  };
  
  const removeAdditionalActivity = (id) => {
    setAdditionalActivities(additionalActivities.filter(item => item.id !== id));
  };
  
  const updateAdditionalActivity = (id, field, value) => {
    setAdditionalActivities(additionalActivities.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };
  
  // Get valid items (non-empty names/durations)
  const validFoodItems = foodItems.filter(item => item.name.trim() !== '');
  const validAdditionalItems = additionalItems.filter(item => item.name.trim() !== '');
  const validActivityItems = activityItems.filter(item => item.duration.trim() !== '');
  const validAdditionalActivities = additionalActivities.filter(item => item.duration.trim() !== '');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (entryType === 'food') {
        if (isEditing) {
          // Editing single item
          const foodData = {
            name,
            personId,
            date,
            mealType,
            sugar: hasSugar,
            carbs: hasCarbs,
          };
          await onUpdateFood(editItem.id, foodData);
          
          // Also create any additional items
          if (validAdditionalItems.length > 0) {
            const additionalEntries = validAdditionalItems.map(item => ({
              name: item.name.trim(),
              personId,
              date,
              mealType,
              sugar: item.sugar,
              carbs: item.carbs,
            }));
            await onAddFoods(additionalEntries);
          }
        } else {
          // Adding multiple items
          if (validFoodItems.length === 0) {
            alert('Please enter at least one food item');
            setIsSubmitting(false);
            return;
          }
          
          const entries = validFoodItems.map(item => ({
            name: item.name.trim(),
            personId,
            date,
            mealType,
            sugar: item.sugar,
            carbs: item.carbs,
          }));
          
          await onAddFoods(entries);
          
          // Remember the meal type for next time
          if (onMealTypeChange) {
            onMealTypeChange(mealType);
          }
        }
      } else {
        if (isEditing) {
          // Editing single activity
          const activityData = {
            name: activityType,
            personId,
            date,
            duration,
            durationMinutes: parseInt(duration) || 0,
            type: activityType,
          };
          await onUpdateActivity(editItem.id, activityData);
          
          // Also create any additional activities
          if (validAdditionalActivities.length > 0) {
            for (const item of validAdditionalActivities) {
              await onAddActivity({
                name: item.type,
                personId,
                date,
                duration: item.duration,
                durationMinutes: parseInt(item.duration) || 0,
                type: item.type,
              });
            }
          }
        } else {
          // Adding multiple activities
          if (validActivityItems.length === 0) {
            alert('Please enter at least one activity');
            setIsSubmitting(false);
            return;
          }
          
          for (const item of validActivityItems) {
            await onAddActivity({
              name: item.type,
              personId,
              date,
              duration: item.duration,
              durationMinutes: parseInt(item.duration) || 0,
              type: item.type,
            });
          }
        }
      }
      
      onClose();
    } catch (error) {
      alert('Error saving entry: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      if (entryType === 'food') {
        await onDeleteFood(editItem.id);
      } else {
        await onDeleteActivity(editItem.id);
      }
      onClose();
    } catch (error) {
      alert('Error deleting entry: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-normal" style={{ color: '#2C3E50' }}>
              {isEditing ? 'Edit Entry' : 'Add Entry'}
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>
          
          {!isEditing && (
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setEntryType('food')}
                className={`flex-1 py-2 px-4 rounded text-sm font-medium transition-colors ${
                  entryType === 'food' 
                    ? 'bg-gray-800 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                🍽️ Food / Drink
              </button>
              <button
                type="button"
                onClick={() => setEntryType('activity')}
                className={`flex-1 py-2 px-4 rounded text-sm font-medium transition-colors ${
                  entryType === 'activity' 
                    ? 'bg-gray-800 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                🏃 Activity
              </button>
            </div>
          )}
          
          {isEditing && (
            <div className="mb-4 px-3 py-2 bg-gray-100 rounded text-sm text-gray-600" style={{ fontFamily: 'system-ui, sans-serif' }}>
              Editing {entryType === 'food' ? '🍽️ Food / Drink' : '🏃 Activity'} entry
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                Family Member
              </label>
              <select
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
                style={{ borderColor: '#E5E2DC' }}
                required
              >
                {familyMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                Date
              </label>
              <select
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
                style={{ borderColor: '#E5E2DC' }}
                required
              >
                {weekDays.map(day => (
                  <option key={day.fullDate} value={day.fullDate}>
                    {day.name} ({day.dateObj.toLocaleDateString('en-US', { month: 'short' })} {day.date})
                  </option>
                ))}
              </select>
            </div>
            
            {entryType === 'food' ? (
              <>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                    Meal Type
                  </label>
                  <select
                    value={mealType}
                    onChange={(e) => handleMealTypeChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                    style={{ borderColor: '#E5E2DC' }}
                  >
                    <option value="Breakfast">Breakfast</option>
                    <option value="Lunch">Lunch</option>
                    <option value="Dinner">Dinner</option>
                    <option value="Snack">Snack</option>
                  </select>
                </div>
                
                {isEditing ? (
                  // Single item editing
                  <>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                        Food / Drink
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Scrambled eggs, Coffee"
                        className="w-full px-3 py-2 border rounded text-sm"
                        style={{ borderColor: '#E5E2DC' }}
                        required
                      />
                    </div>
                    
                    <div className="flex gap-4 flex-wrap">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isDrink}
                          onChange={(e) => setIsDrink(e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                          🥤 Drink
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hasSugar}
                          onChange={(e) => setHasSugar(e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm bg-rose-100 text-rose-800 px-2 py-0.5 rounded">
                          Processed Sugar
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={hasCarbs}
                          onChange={(e) => setHasCarbs(e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          Heavy Carbs
                        </span>
                      </label>
                    </div>
                    
                    {/* Additional items section for edit mode */}
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E5E2DC' }}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                          Add More to {mealType}
                        </label>
                      </div>
                      
                      {additionalItems.length === 0 ? (
                        <button
                          type="button"
                          onClick={addAdditionalItem}
                          className="w-full py-2 px-3 border-2 border-dashed rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
                          style={{ borderColor: '#E5E2DC' }}
                        >
                          <span>+</span>
                          <span>Add another item</span>
                        </button>
                      ) : (
                        <div className="space-y-3">
                          {additionalItems.map((item, index) => (
                            <div 
                              key={item.id} 
                              className="border rounded-lg p-3"
                              style={{ borderColor: '#E5E2DC', backgroundColor: '#FDFCFA' }}
                            >
                              <div className="flex gap-2 mb-2">
                                <input
                                  type="text"
                                  value={item.name}
                                  onChange={(e) => updateAdditionalItem(item.id, 'name', e.target.value)}
                                  placeholder="Add another item..."
                                  className="flex-1 px-3 py-2 border rounded text-sm"
                                  style={{ borderColor: '#E5E2DC' }}
                                  autoFocus={index === additionalItems.length - 1}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAdditionalItem(item.id)}
                                  className="px-2 text-gray-400 hover:text-red-500 transition-colors"
                                  title="Remove item"
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="flex gap-3 flex-wrap">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={item.isDrink}
                                    onChange={(e) => updateAdditionalItem(item.id, 'isDrink', e.target.checked)}
                                    className="w-3.5 h-3.5 rounded"
                                  />
                                  <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                                    🥤 Drink
                                  </span>
                                </label>
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={item.sugar}
                                    onChange={(e) => updateAdditionalItem(item.id, 'sugar', e.target.checked)}
                                    className="w-3.5 h-3.5 rounded"
                                  />
                                  <span className="text-xs bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded">
                                    Processed Sugar
                                  </span>
                                </label>
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={item.carbs}
                                    onChange={(e) => updateAdditionalItem(item.id, 'carbs', e.target.checked)}
                                    className="w-3.5 h-3.5 rounded"
                                  />
                                  <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                                    Carbs
                                  </span>
                                </label>
                              </div>
                            </div>
                          ))}
                          
                          <button
                            type="button"
                            onClick={addAdditionalItem}
                            className="w-full py-2 px-3 border-2 border-dashed rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
                            style={{ borderColor: '#E5E2DC' }}
                          >
                            <span>+</span>
                            <span>Add another item</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // Multi-item adding
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                      Food / Drink Items
                    </label>
                    <div className="space-y-3">
                      {foodItems.map((item, index) => (
                        <div 
                          key={item.id} 
                          className="border rounded-lg p-3"
                          style={{ borderColor: '#E5E2DC', backgroundColor: '#FDFCFA' }}
                        >
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => updateFoodItem(item.id, 'name', e.target.value)}
                              placeholder={index === 0 ? "e.g., Scrambled eggs, Coffee" : "Add another item..."}
                              className="flex-1 px-3 py-2 border rounded text-sm"
                              style={{ borderColor: '#E5E2DC' }}
                              autoFocus={index === foodItems.length - 1 && foodItems.length > 1}
                            />
                            {foodItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeFoodItem(item.id)}
                                className="px-2 text-gray-400 hover:text-red-500 transition-colors"
                                title="Remove item"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          <div className="flex gap-3 flex-wrap">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={item.isDrink}
                                onChange={(e) => updateFoodItem(item.id, 'isDrink', e.target.checked)}
                                className="w-3.5 h-3.5 rounded"
                              />
                              <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                                🥤 Drink
                              </span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={item.sugar}
                                onChange={(e) => updateFoodItem(item.id, 'sugar', e.target.checked)}
                                className="w-3.5 h-3.5 rounded"
                              />
                              <span className="text-xs bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded">
                                Processed Sugar
                              </span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={item.carbs}
                                onChange={(e) => updateFoodItem(item.id, 'carbs', e.target.checked)}
                                className="w-3.5 h-3.5 rounded"
                              />
                              <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                                Carbs
                              </span>
                            </label>
                          </div>
                        </div>
                      ))}
                      
                      <button
                        type="button"
                        onClick={addFoodItem}
                        className="w-full py-2 px-3 border-2 border-dashed rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
                        style={{ borderColor: '#E5E2DC' }}
                      >
                        <span className="text-lg leading-none">+</span>
                        <span>Add another item</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Activity form
              <>
                {isEditing ? (
                  // Single activity editing
                  <>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                        Activity Type
                      </label>
                      <select
                        value={activityType}
                        onChange={(e) => setActivityType(e.target.value)}
                        className="w-full px-3 py-2 border rounded text-sm"
                        style={{ borderColor: '#E5E2DC' }}
                      >
                        <option value="Cardio">Cardio</option>
                        <option value="Strength">Strength</option>
                        <option value="Flexibility">Flexibility</option>
                        <option value="Sports">Sports</option>
                        <option value="Walking">Walking</option>
                        <option value="Swimming">Swimming</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                        Duration
                      </label>
                      <input
                        type="text"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        placeholder="e.g., 30 min or 1 mile"
                        className="w-full px-3 py-2 border rounded text-sm"
                        style={{ borderColor: '#E5E2DC' }}
                        required
                      />
                    </div>
                    
                    {/* Additional activities section for edit mode */}
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E5E2DC' }}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                          Add More Activities
                        </label>
                      </div>
                      
                      {additionalActivities.length === 0 ? (
                        <button
                          type="button"
                          onClick={addAdditionalActivity}
                          className="w-full py-2 px-3 border-2 border-dashed rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
                          style={{ borderColor: '#E5E2DC' }}
                        >
                          <span>+</span>
                          <span>Add another activity</span>
                        </button>
                      ) : (
                        <div className="space-y-3">
                          {additionalActivities.map((item, index) => (
                            <div 
                              key={item.id} 
                              className="border rounded-lg p-3"
                              style={{ borderColor: '#E5E2DC', backgroundColor: '#FDFCFA' }}
                            >
                              <div className="flex gap-2 mb-2">
                                <select
                                  value={item.type}
                                  onChange={(e) => updateAdditionalActivity(item.id, 'type', e.target.value)}
                                  className="flex-1 px-3 py-2 border rounded text-sm"
                                  style={{ borderColor: '#E5E2DC' }}
                                >
                                  <option value="Cardio">Cardio</option>
                                  <option value="Strength">Strength</option>
                                  <option value="Flexibility">Flexibility</option>
                                  <option value="Sports">Sports</option>
                                  <option value="Walking">Walking</option>
                                  <option value="Swimming">Swimming</option>
                                  <option value="Other">Other</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => removeAdditionalActivity(item.id)}
                                  className="px-2 text-gray-400 hover:text-red-500 transition-colors"
                                  title="Remove activity"
                                >
                                  ✕
                                </button>
                              </div>
                              <input
                                type="text"
                                value={item.duration}
                                onChange={(e) => updateAdditionalActivity(item.id, 'duration', e.target.value)}
                                placeholder="e.g., 30 min or 1 mile"
                                className="w-full px-3 py-2 border rounded text-sm"
                                style={{ borderColor: '#E5E2DC' }}
                                autoFocus={index === additionalActivities.length - 1}
                              />
                            </div>
                          ))}
                          
                          <button
                            type="button"
                            onClick={addAdditionalActivity}
                            className="w-full py-2 px-3 border-2 border-dashed rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
                            style={{ borderColor: '#E5E2DC' }}
                          >
                            <span>+</span>
                            <span>Add another activity</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // Multi-activity adding
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}>
                      Activities
                    </label>
                    <div className="space-y-3">
                      {activityItems.map((item, index) => (
                        <div 
                          key={item.id} 
                          className="border rounded-lg p-3"
                          style={{ borderColor: '#E5E2DC', backgroundColor: '#FDFCFA' }}
                        >
                          <div className="flex gap-2 mb-2">
                            <select
                              value={item.type}
                              onChange={(e) => updateActivityItem(item.id, 'type', e.target.value)}
                              className="flex-1 px-3 py-2 border rounded text-sm"
                              style={{ borderColor: '#E5E2DC' }}
                            >
                              <option value="Cardio">Cardio</option>
                              <option value="Strength">Strength</option>
                              <option value="Flexibility">Flexibility</option>
                              <option value="Sports">Sports</option>
                              <option value="Walking">Walking</option>
                              <option value="Swimming">Swimming</option>
                              <option value="Other">Other</option>
                            </select>
                            {activityItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeActivityItem(item.id)}
                                className="px-2 text-gray-400 hover:text-red-500 transition-colors"
                                title="Remove activity"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            value={item.duration}
                            onChange={(e) => updateActivityItem(item.id, 'duration', e.target.value)}
                            placeholder={index === 0 ? "e.g., 30 min or 1 mile" : "Duration..."}
                            className="w-full px-3 py-2 border rounded text-sm"
                            style={{ borderColor: '#E5E2DC' }}
                            autoFocus={index === activityItems.length - 1 && activityItems.length > 1}
                          />
                        </div>
                      ))}
                      
                      <button
                        type="button"
                        onClick={addActivityItem}
                        className="w-full py-2 px-3 border-2 border-dashed rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
                        style={{ borderColor: '#E5E2DC' }}
                      >
                        <span className="text-lg leading-none">+</span>
                        <span>Add another activity</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-3 rounded text-white font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#2C3E50' }}
              >
                {isSubmitting 
                  ? 'Saving...' 
                  : isEditing 
                    ? (entryType === 'food' && validAdditionalItems.length > 0)
                      ? `Save + Add ${validAdditionalItems.length} More`
                      : (entryType === 'activity' && validAdditionalActivities.length > 0)
                        ? `Save + Add ${validAdditionalActivities.length} More`
                        : 'Save Changes'
                    : entryType === 'food' && validFoodItems.length > 1
                      ? `Add ${validFoodItems.length} Items`
                      : entryType === 'activity' && validActivityItems.length > 1
                        ? `Add ${validActivityItems.length} Activities`
                        : 'Add Entry'
                }
              </button>
              
              {isEditing && !showDeleteConfirm && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isSubmitting}
                  className="px-4 py-3 rounded font-medium transition-colors hover:bg-red-100 disabled:opacity-50 text-red-600 border border-red-200"
                >
                  Delete
                </button>
              )}
            </div>
            
            {isEditing && showDeleteConfirm && (
              <div className="bg-red-50 border border-red-200 rounded p-4">
                <p className="text-sm text-red-800 mb-3">Are you sure you want to delete this entry?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isSubmitting}
                    className="flex-1 py-2 rounded font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Deleting...' : 'Yes, Delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isSubmitting}
                    className="flex-1 py-2 rounded font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================
// WEEKLY VIEW COMPONENTS
// ============================================
const WeeklyMealLine = ({ label, mealType, items, onItemClick, onMealTitleClick }) => {
  const handleTitleClick = (e) => {
    e.stopPropagation();
    if (onMealTitleClick) {
      onMealTitleClick(mealType);
    }
  };
  
  if (!items || items.length === 0) {
    return (
      <div className="flex items-start gap-2 py-1">
        <button
          onClick={handleTitleClick}
          className="text-xs font-semibold w-4 flex-shrink-0 hover:bg-gray-200 rounded px-0.5 -mx-0.5 transition-colors cursor-pointer"
          style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
          title={`Add ${mealType}`}
        >
          {label}
        </button>
        <button
          onClick={handleTitleClick}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          title={`Add ${mealType}`}
        >
          +
        </button>
      </div>
    );
  }
  
  return (
    <div className="flex items-start gap-2 py-1">
      <button
        onClick={handleTitleClick}
        className="text-xs font-semibold w-4 flex-shrink-0 hover:bg-gray-200 rounded px-0.5 -mx-0.5 transition-colors cursor-pointer"
        style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}
        title={`Add ${mealType}`}
      >
        {label}
      </button>
      <div className="text-xs text-gray-700 leading-relaxed">
        {items.map((item, idx) => (
          <span key={item.id || idx}>
            <span 
              className={getItemStyle(item, true)}
              onClick={() => onItemClick(item)}
            >
              {item.name}
            </span>
            {idx < items.length - 1 && <span className="text-gray-400">, </span>}
          </span>
        ))}
      </div>
    </div>
  );
};

const WeeklyActivityLine = ({ activities, onItemClick, onAddActivity }) => {
  return (
    <div className="mt-2 pt-2 border-t border-dashed" style={{ borderColor: '#E5E2DC' }}>
      <div className="flex items-start gap-2 py-0.5">
        <button
          onClick={onAddActivity}
          className="text-xs font-semibold w-4 flex-shrink-0 hover:bg-gray-200 rounded px-0.5 -mx-0.5 transition-colors cursor-pointer"
          style={{ fontFamily: 'system-ui, sans-serif', color: activities?.length ? '#6B6560' : '#9B9590' }}
          title="Add Activity"
        >
          A
        </button>
        {(!activities || activities.length === 0) ? (
          <button
            onClick={onAddActivity}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            title="Add Activity"
          >
            +
          </button>
        ) : (
          <div className="text-xs text-gray-700 leading-relaxed">
            {activities.map((activity, idx) => (
              <span key={activity.id || idx}>
                <span 
                  className="cursor-pointer hover:bg-gray-100 rounded px-0.5 -mx-0.5 transition-colors"
                  style={{ color: '#4A6741' }}
                  onClick={() => onItemClick(activity)}
                >
                  {activity.name}
                </span>
                <span className="text-gray-400 ml-1">({activity.duration})</span>
                {idx < activities.length - 1 && <span className="text-gray-400">, </span>}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const WeeklyDayCell = ({ data, isEven, onItemClick, onMealTitleClick, onAddActivity }) => {
  const dayData = data || {};
  
  return (
    <div 
      className="p-3 border-r last:border-r-0 min-h-[140px]"
      style={{ borderColor: '#E5E2DC', backgroundColor: isEven ? '#FDFCFA' : 'white' }}
    >
      {mealTypes.map(mealType => (
        <WeeklyMealLine 
          key={mealType} 
          label={mealLabels[mealType]} 
          mealType={mealTypeToAirtable[mealType]}
          items={dayData[mealType]} 
          onItemClick={onItemClick}
          onMealTitleClick={onMealTitleClick}
        />
      ))}
      <WeeklyActivityLine activities={dayData.Activity} onItemClick={onItemClick} onAddActivity={onAddActivity} />
    </div>
  );
};

const getWeeklyStats = (personData, weekDays) => {
  let totalItems = 0;
  let sugarItems = 0;
  let carbItems = 0;
  let activeDays = 0;
  
  weekDays.forEach(day => {
    const dayData = personData?.[day.name];
    if (!dayData) return;
    
    mealTypes.forEach(mealType => {
      const items = dayData[mealType] || [];
      totalItems += items.length;
      sugarItems += items.filter(item => item.sugar).length;
      carbItems += items.filter(item => item.carbs).length;
    });
    
    if (dayData.Activity && dayData.Activity.length > 0) {
      activeDays++;
    }
  });
  
  return { totalItems, sugarItems, carbItems, activeDays };
};

// Weekly View Component (Responsive)
const WeeklyView = ({ familyMembers, weekData, weekDays, onItemClick, onMealTitleClick, selectedPersonIndex, setSelectedPersonIndex }) => {
  const selectedMember = familyMembers[selectedPersonIndex] || familyMembers[0];
  
  // Handler for mobile meal title clicks
  const handleMobileMealTitleClick = (day, mealType) => {
    if (onMealTitleClick && selectedMember) {
      onMealTitleClick(selectedMember.id, day.fullDate, mealType);
    }
  };
  
  return (
    <>
      {/* Mobile View - One Person at a Time */}
      <div className="md:hidden">
        {/* Person Selector - Horizontal Avatar List */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-3 pt-1 -mx-4 px-4">
          {familyMembers.map((member, idx) => (
            <button
              key={member.id}
              onClick={() => setSelectedPersonIndex(idx)}
              className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all flex-shrink-0 ${
                idx === selectedPersonIndex 
                  ? 'bg-white shadow-md' 
                  : 'bg-white/50 hover:bg-white'
              }`}
              style={idx === selectedPersonIndex ? { 
                boxShadow: `0 0 0 2px ${member.color}, 0 4px 6px -1px rgba(0, 0, 0, 0.1)` 
              } : {}}
            >
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium transition-transform ${
                  idx === selectedPersonIndex ? 'scale-110' : ''
                }`}
                style={{ backgroundColor: member.color }}
              >
                {member.initials}
              </div>
              <span 
                className={`text-sm font-medium transition-colors ${
                  idx === selectedPersonIndex ? '' : 'text-gray-500'
                }`}
                style={idx === selectedPersonIndex ? { color: member.color } : {}}
              >
                {member.name}
              </span>
            </button>
          ))}
        </div>
        
        {/* Days for selected person */}
        <div className="space-y-3">
          {weekDays.map((day, dayIdx) => {
            const dayData = weekData[selectedMember?.id]?.[day.name] || {};
            const hasActivity = dayData.Activity && dayData.Activity.length > 0;
            
            return (
              <div 
                key={day.name}
                className="bg-white rounded-lg overflow-hidden"
                style={{ border: '1px solid #E5E2DC' }}
              >
                {/* Day Header */}
                <div 
                  className="px-3 py-2 flex items-center justify-between"
                  style={{ backgroundColor: dayIdx % 2 === 0 ? '#FDFCFA' : 'white', borderBottom: '1px solid #E5E2DC' }}
                >
                  <div>
                    <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                      {day.name}
                    </span>
                    <span 
                      className="text-xs ml-2"
                      style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
                    >
                      {day.dateObj.toLocaleDateString('en-US', { month: 'short' })} {day.date}
                    </span>
                  </div>
                  {hasActivity && (
                    <span style={{ color: '#4A6741' }}>●</span>
                  )}
                </div>
                
                {/* Meals Grid */}
                <div className="p-3">
                  <div className="grid grid-cols-2 gap-3">
                    {mealTypes.map(mealType => {
                      const items = dayData[mealType] || [];
                      const airtableMealType = mealTypeToAirtable[mealType];
                      return (
                        <div key={mealType}>
                          <button
                            onClick={() => handleMobileMealTitleClick(day, airtableMealType)}
                            className="text-[10px] font-semibold uppercase tracking-wider mb-2 hover:bg-gray-200 rounded px-1 -mx-1 py-0.5 transition-colors cursor-pointer block"
                            style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
                            title={`Add ${mealType}`}
                          >
                            {mealType}
                          </button>
                          {items.length === 0 ? (
                            <button
                              onClick={() => handleMobileMealTitleClick(day, airtableMealType)}
                              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5 transition-colors cursor-pointer"
                            >
                              <span className="text-sm leading-none">+</span>
                              <span>Add</span>
                            </button>
                          ) : (
                            <div className="space-y-0.5">
                              {items.map((item, idx) => (
                                <div 
                                  key={item.id || idx}
                                  className="text-xs cursor-pointer"
                                  onClick={() => onItemClick(item)}
                                >
                                  <span className={getItemStyle(item, true)}>{item.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Activity - Always show section */}
                  <div className="mt-3 pt-2 border-t border-dashed" style={{ borderColor: '#E5E2DC' }}>
                    <button
                      onClick={() => onMealTitleClick && onMealTitleClick(selectedMember.id, day.fullDate, 'activity')}
                      className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 hover:bg-gray-200 rounded px-1 -mx-1 py-0.5 transition-colors cursor-pointer block"
                      style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
                      title="Add Activity"
                    >
                      Activity
                    </button>
                    {hasActivity ? (
                      dayData.Activity.map((activity, idx) => (
                        <div 
                          key={activity.id || idx}
                          className="text-xs flex items-center gap-1.5 cursor-pointer"
                          onClick={() => onItemClick(activity)}
                        >
                          <span style={{ color: '#4A6741' }}>●</span>
                          <span>{activity.name}</span>
                          <span className="text-gray-400">· {activity.duration}</span>
                        </div>
                      ))
                    ) : (
                      <button
                        onClick={() => onMealTitleClick && onMealTitleClick(selectedMember.id, day.fullDate, 'activity')}
                        className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5 transition-colors cursor-pointer"
                      >
                        <span className="text-sm leading-none">+</span>
                        <span>Add</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Mobile Weekly Summary for selected person */}
        <div className="mt-6">
          <div 
            className="text-xs font-semibold uppercase tracking-widest mb-3"
            style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
          >
            Weekly Summary
          </div>
          {selectedMember && (() => {
            const stats = getWeeklyStats(weekData[selectedMember.id], weekDays);
            const sugarPercent = stats.totalItems > 0 
              ? Math.round((stats.sugarItems / stats.totalItems) * 100) 
              : 0;
            const carbPercent = stats.totalItems > 0 
              ? Math.round((stats.carbItems / stats.totalItems) * 100) 
              : 0;
            
            return (
              <div 
                className="bg-white rounded-lg p-4"
                style={{ border: '1px solid #E5E2DC', borderTop: `3px solid ${selectedMember.color}` }}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs block mb-1" style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}>
                      Items logged
                    </span>
                    <span className="text-2xl font-normal" style={{ color: '#4A4540' }}>
                      {stats.totalItems}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs block mb-1" style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}>
                      Active days
                    </span>
                    <span className="text-2xl font-normal" style={{ color: '#4A6741' }}>
                      {stats.activeDays}<span className="text-sm text-gray-400">/7</span>
                    </span>
                  </div>
                  <div>
                    <span className="text-xs block mb-1" style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}>
                      Processed sugar items
                    </span>
                    <span className="text-2xl font-normal" style={{ color: '#BE123C' }}>
                      {stats.sugarItems}
                      <span className="text-xs ml-1 text-gray-400">({sugarPercent}%)</span>
                    </span>
                  </div>
                  <div>
                    <span className="text-xs block mb-1" style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}>
                      Heavy carbs
                    </span>
                    <span className="text-2xl font-normal" style={{ color: '#B45309' }}>
                      {stats.carbItems}
                      <span className="text-xs ml-1 text-gray-400">({carbPercent}%)</span>
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      
      {/* Desktop View - Full Table */}
      <div className="hidden md:block">
        <div 
          className="bg-white rounded shadow-sm overflow-hidden"
          style={{ 
            boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
            border: '1px solid #E5E2DC'
          }}
        >
          {/* Header Row */}
          <div 
            className="grid border-b sticky top-0 z-10"
            style={{ 
              gridTemplateColumns: `100px repeat(${familyMembers.length || 4}, 1fr)`,
              borderColor: '#E5E2DC',
              backgroundColor: '#FDFCFA'
            }}
          >
            <div className="p-4 border-r" style={{ borderColor: '#E5E2DC' }}>
              <span 
                className="text-[10px] font-semibold uppercase tracking-widest"
                style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
              >
                Week
              </span>
            </div>
            {familyMembers.map(member => (
              <div 
                key={member.id}
                className="p-4 border-r last:border-r-0 text-center"
                style={{ borderColor: '#E5E2DC' }}
              >
                <div 
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full text-white text-sm font-normal mb-1"
                  style={{ backgroundColor: member.color }}
                >
                  {member.initials}
                </div>
                <div className="text-sm font-medium" style={{ color: member.color }}>
                  {member.name}
                </div>
              </div>
            ))}
          </div>
          
          {/* Day Rows */}
          {weekDays.map((day, dayIdx) => (
            <div 
              key={day.name}
              className="grid border-b last:border-b-0"
              style={{ 
                gridTemplateColumns: `100px repeat(${familyMembers.length || 4}, 1fr)`,
                borderColor: '#E5E2DC'
              }}
            >
              <div 
                className="p-3 border-r flex flex-col justify-start"
                style={{ 
                  borderColor: '#E5E2DC',
                  backgroundColor: dayIdx % 2 === 0 ? '#FDFCFA' : 'white'
                }}
              >
                <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                  {day.short}
                </span>
                <span 
                  className="text-xs"
                  style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
                >
                  {day.dateObj.toLocaleDateString('en-US', { month: 'short' })} {day.date}
                </span>
              </div>
              
              {familyMembers.map(member => (
                <WeeklyDayCell 
                  key={member.id}
                  data={weekData[member.id]?.[day.name]}
                  isEven={dayIdx % 2 === 0}
                  onItemClick={onItemClick}
                  onMealTitleClick={(mealType) => onMealTitleClick && onMealTitleClick(member.id, day.fullDate, mealType)}
                  onAddActivity={() => onMealTitleClick && onMealTitleClick(member.id, day.fullDate, 'activity')}
                />
              ))}
            </div>
          ))}
        </div>
        
        {/* Weekly Summary - Desktop */}
        <div className="mt-6">
          <div 
            className="text-xs font-semibold uppercase tracking-widest mb-3"
            style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
          >
            Weekly Summary
          </div>
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${familyMembers.length}, 1fr)` }}>
            {familyMembers.map(member => {
              const stats = getWeeklyStats(weekData[member.id], weekDays);
              const sugarPercent = stats.totalItems > 0 
                ? Math.round((stats.sugarItems / stats.totalItems) * 100) 
                : 0;
              const carbPercent = stats.totalItems > 0 
                ? Math.round((stats.carbItems / stats.totalItems) * 100) 
                : 0;
              
              return (
                <div 
                  key={member.id}
                  className="bg-white rounded p-4"
                  style={{ border: '1px solid #E5E2DC', borderTop: `3px solid ${member.color}` }}
                >
                  <div className="text-sm font-medium mb-3" style={{ color: member.color }}>
                    {member.name}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs" style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}>
                        Items logged
                      </span>
                      <span className="text-lg font-normal" style={{ color: '#4A4540' }}>
                        {stats.totalItems}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs" style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}>
                        Processed sugar
                      </span>
                      <span className="text-lg font-normal" style={{ color: '#BE123C' }}>
                        {stats.sugarItems}
                        <span className="text-xs ml-1" style={{ color: '#8B8680' }}>
                          ({sugarPercent}%)
                        </span>
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs" style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}>
                        Heavy carbs
                      </span>
                      <span className="text-lg font-normal" style={{ color: '#B45309' }}>
                        {stats.carbItems}
                        <span className="text-xs ml-1" style={{ color: '#8B8680' }}>
                          ({carbPercent}%)
                        </span>
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs" style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}>
                        Active days
                      </span>
                      <span className="text-lg font-normal" style={{ color: '#4A6741' }}>
                        {stats.activeDays}
                        <span className="text-xs ml-1" style={{ color: '#8B8680' }}>/ 7</span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================
// DAILY VIEW COMPONENTS
// ============================================
const DailyMealCell = ({ items, mealType, personId, date, onItemClick, onMealTitleClick }) => {
  if (!items || items.length === 0) {
    return (
      <button
        onClick={() => onMealTitleClick && onMealTitleClick(personId, date, mealType)}
        className="text-gray-400 hover:text-gray-600 text-sm flex items-center gap-1 transition-colors cursor-pointer"
      >
        <span className="text-base leading-none">+</span>
        <span>Add items</span>
      </button>
    );
  }
  
  return (
    <div className="space-y-1.5">
      {items.map((item, idx) => (
        <div 
          key={item.id || idx} 
          className="text-sm cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 transition-colors"
          onClick={() => onItemClick(item)}
        >
          <span className={getItemStyle(item, true)}>{item.name}</span>
        </div>
      ))}
    </div>
  );
};

const DailyActivityCell = ({ activities, onItemClick, onAddActivity }) => {
  if (!activities || activities.length === 0) {
    return (
      <button
        onClick={onAddActivity}
        className="text-gray-400 hover:text-gray-600 text-sm flex items-center gap-1 transition-colors cursor-pointer"
      >
        <span className="text-base leading-none">+</span>
        <span>Add activity</span>
      </button>
    );
  }
  
  return (
    <div className="space-y-1.5">
      {activities.map((activity, idx) => (
        <div 
          key={activity.id || idx} 
          className="text-sm flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 transition-colors"
          onClick={() => onItemClick(activity)}
        >
          <span style={{ color: '#4A6741' }}>●</span>
          <span>{activity.name}</span>
          <span className="text-gray-400 text-xs">· {activity.duration}</span>
        </div>
      ))}
    </div>
  );
};

const getDailySugarCount = (personData) => {
  if (!personData) return 0;
  let count = 0;
  mealTypes.forEach(mealType => {
    const items = personData[mealType] || [];
    count += items.filter(item => item.sugar).length;
  });
  return count;
};

// Mobile meal items for quadrant
const MobileMealItems = ({ items, mealType, onItemClick, onAddClick }) => {
  if (!items || items.length === 0) {
    return (
      <button
        onClick={onAddClick}
        className="text-gray-400 hover:text-gray-600 text-xs flex items-center gap-0.5 transition-colors cursor-pointer"
      >
        <span className="text-sm leading-none">+</span>
        <span>Add</span>
      </button>
    );
  }
  
  return (
    <div className="space-y-0.5">
      {items.map((item, idx) => (
        <div 
          key={item.id || idx}
          className="text-xs cursor-pointer"
          onClick={() => onItemClick(item)}
        >
          <span className={getItemStyle(item, true)}>{item.name}</span>
        </div>
      ))}
    </div>
  );
};

// Mobile Person Card for Daily View
const MobileDailyPersonCard = ({ member, dayData, date, onItemClick, onMealTitleClick }) => {
  const sugarCount = getDailySugarCount(dayData);
  const activities = dayData?.Activity || [];
  
  const handleMealTitleClick = (mealType) => {
    if (onMealTitleClick) {
      onMealTitleClick(member.id, date, mealType);
    }
  };
  
  return (
    <div 
      className="bg-white rounded-lg shadow-sm overflow-hidden"
      style={{ border: '1px solid #E5E2DC' }}
    >
      {/* Person Header */}
      <div 
        className="p-3 flex items-center gap-3"
        style={{ backgroundColor: '#FDFCFA', borderBottom: '1px solid #E5E2DC' }}
      >
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
          style={{ backgroundColor: member.color }}
        >
          {member.initials}
        </div>
        <div className="flex-1">
          <div className="text-base font-medium" style={{ color: member.color }}>
            {member.name}
          </div>
          {sugarCount > 0 && (
            <div 
              className="text-xs"
              style={{ fontFamily: 'system-ui, sans-serif', color: '#BE123C' }}
            >
              {sugarCount} Processed sugar item{sugarCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
      
      {/* 2x2 Meal Grid */}
      <div className="grid grid-cols-2">
        {/* Breakfast */}
        <div className="p-3 border-r border-b" style={{ borderColor: '#E5E2DC' }}>
          <button
            onClick={() => handleMealTitleClick('Breakfast')}
            className="text-[10px] font-semibold uppercase tracking-wider mb-2 hover:bg-gray-200 rounded px-1 -mx-1 py-0.5 transition-colors cursor-pointer block"
            style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
            title="Add Breakfast"
          >
            Breakfast
          </button>
          <MobileMealItems items={dayData?.Breakfast} onItemClick={onItemClick} onAddClick={() => handleMealTitleClick('Breakfast')} />
        </div>
        
        {/* Lunch */}
        <div className="p-3 border-b" style={{ borderColor: '#E5E2DC' }}>
          <button
            onClick={() => handleMealTitleClick('Lunch')}
            className="text-[10px] font-semibold uppercase tracking-wider mb-2 hover:bg-gray-200 rounded px-1 -mx-1 py-0.5 transition-colors cursor-pointer block"
            style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
            title="Add Lunch"
          >
            Lunch
          </button>
          <MobileMealItems items={dayData?.Lunch} onItemClick={onItemClick} onAddClick={() => handleMealTitleClick('Lunch')} />
        </div>
        
        {/* Dinner */}
        <div className="p-3 border-r" style={{ borderColor: '#E5E2DC' }}>
          <button
            onClick={() => handleMealTitleClick('Dinner')}
            className="text-[10px] font-semibold uppercase tracking-wider mb-2 hover:bg-gray-200 rounded px-1 -mx-1 py-0.5 transition-colors cursor-pointer block"
            style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
            title="Add Dinner"
          >
            Dinner
          </button>
          <MobileMealItems items={dayData?.Dinner} onItemClick={onItemClick} onAddClick={() => handleMealTitleClick('Dinner')} />
        </div>
        
        {/* Snacks */}
        <div className="p-3">
          <button
            onClick={() => handleMealTitleClick('Snack')}
            className="text-[10px] font-semibold uppercase tracking-wider mb-2 hover:bg-gray-200 rounded px-1 -mx-1 py-0.5 transition-colors cursor-pointer block"
            style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
            title="Add Snack"
          >
            Snacks
          </button>
          <MobileMealItems items={dayData?.Snacks} onItemClick={onItemClick} onAddClick={() => handleMealTitleClick('Snack')} />
        </div>
      </div>
      
      {/* Activity Section - Always show */}
      <div 
        className="p-3"
        style={{ backgroundColor: '#FDFCFA', borderTop: '1px solid #E5E2DC' }}
      >
        <button
          onClick={() => onMealTitleClick && onMealTitleClick(member.id, date, 'activity')}
          className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 hover:bg-gray-200 rounded px-1 -mx-1 py-0.5 transition-colors cursor-pointer block"
          style={{ fontFamily: 'system-ui, sans-serif', color: '#9B9590' }}
          title="Add Activity"
        >
          Activity
        </button>
        {activities.length > 0 ? (
          activities.map((activity, idx) => (
            <div 
              key={activity.id || idx}
              className="text-xs flex items-center gap-1.5 cursor-pointer"
              onClick={() => onItemClick(activity)}
            >
              <span style={{ color: '#4A6741' }}>●</span>
              <span>{activity.name}</span>
              <span className="text-gray-400">· {activity.duration}</span>
            </div>
          ))
        ) : (
          <button
            onClick={() => onMealTitleClick && onMealTitleClick(member.id, date, 'activity')}
            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5 transition-colors cursor-pointer"
          >
            <span className="text-sm leading-none">+</span>
            <span>Add</span>
          </button>
        )}
      </div>
    </div>
  );
};

// Daily View Component (Responsive)
const DailyView = ({ familyMembers, weekData, weekDays, currentDayIndex, setCurrentDayIndex, onItemClick, onMealTitleClick, onWeekChange, onGoToToday, isToday }) => {
  const currentDay = weekDays[currentDayIndex];
  
  return (
    <>
      {/* Day Navigation - Date with Arrows */}
      <div className="flex justify-center mb-4 md:mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              if (currentDayIndex === 0) {
                onWeekChange(-1);
                setCurrentDayIndex(6);
              } else {
                setCurrentDayIndex(prev => prev - 1);
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            style={{ color: '#6B6560' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div className="text-center min-w-[180px]">
            <div className="text-lg font-medium" style={{ color: '#2C3E50' }}>
              {currentDay?.name}
            </div>
            <div 
              className="text-sm"
              style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}
            >
              {currentDay?.dateObj?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            {!isToday && (
              <button
                onClick={onGoToToday}
                className="text-xs text-blue-500 hover:underline mt-1"
                style={{ fontFamily: 'system-ui, sans-serif' }}
              >
                Go to today
              </button>
            )}
          </div>
          
          <button 
            onClick={() => {
              if (currentDayIndex === 6) {
                onWeekChange(1);
                setCurrentDayIndex(0);
              } else {
                setCurrentDayIndex(prev => prev + 1);
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            style={{ color: '#6B6560' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Mobile View - Card Stack */}
      <div className="md:hidden space-y-4">
        {familyMembers.map((member) => {
          const dayData = weekData[member.id]?.[currentDay?.name] || {};
          return (
            <MobileDailyPersonCard
              key={member.id}
              member={member}
              dayData={dayData}
              date={currentDay?.fullDate}
              onItemClick={onItemClick}
              onMealTitleClick={onMealTitleClick}
            />
          );
        })}
      </div>
      
      {/* Desktop View - Table */}
      <div 
        className="hidden md:block bg-white rounded shadow-sm overflow-hidden"
        style={{ 
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
          border: '1px solid #E5E2DC'
        }}
      >
        {/* Table Header */}
        <div 
          className="grid border-b"
          style={{ 
            gridTemplateColumns: '140px repeat(5, 1fr)',
            borderColor: '#E5E2DC',
            backgroundColor: '#FDFCFA'
          }}
        >
          <div className="p-4 border-r" style={{ borderColor: '#E5E2DC' }} />
          {[...mealTypes, 'Activity'].map(header => {
            const isActivity = header === 'Activity';
            const airtableMealType = !isActivity ? mealTypeToAirtable[header] : null;
            
            return (
              <div 
                key={header}
                className="p-4 text-center border-r last:border-r-0"
                style={{ borderColor: '#E5E2DC' }}
              >
                {isActivity ? (
                  <span 
                    className="text-xs font-semibold uppercase tracking-widest"
                    style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}
                  >
                    {header}
                  </span>
                ) : (
                  <span 
                    className="text-xs font-semibold uppercase tracking-widest"
                    style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}
                  >
                    {header}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Person Rows */}
        {familyMembers.map((member) => {
          const dayData = weekData[member.id]?.[currentDay?.name] || {};
          const sugarCount = getDailySugarCount(dayData);
          
          return (
            <div 
              key={member.id}
              className="grid border-b last:border-b-0"
              style={{ 
                gridTemplateColumns: '140px repeat(5, 1fr)',
                borderColor: '#E5E2DC'
              }}
            >
              {/* Person Label */}
              <div 
                className="p-4 border-r flex items-start gap-3"
                style={{ borderColor: '#E5E2DC', backgroundColor: '#FDFCFA' }}
              >
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-normal flex-shrink-0"
                  style={{ backgroundColor: member.color }}
                >
                  {member.initials}
                </div>
                <div>
                  <div className="text-sm font-medium" style={{ color: member.color }}>
                    {member.name}
                  </div>
                  {sugarCount > 0 && (
                    <div 
                      className="text-xs mt-0.5"
                      style={{ fontFamily: 'system-ui, sans-serif', color: '#BE123C' }}
                    >
                      {sugarCount} Processed sugar
                    </div>
                  )}
                </div>
              </div>
              
              {/* Meal Columns */}
              {mealTypes.map(mealType => {
                const airtableMealType = mealTypeToAirtable[mealType];
                return (
                  <div 
                    key={mealType}
                    className="p-4 border-r"
                    style={{ borderColor: '#E5E2DC' }}
                  >
                    <DailyMealCell 
                      items={dayData[mealType]} 
                      mealType={airtableMealType}
                      personId={member.id}
                      date={currentDay?.fullDate}
                      onItemClick={onItemClick}
                      onMealTitleClick={onMealTitleClick}
                    />
                  </div>
                );
              })}
              
              {/* Activity Column */}
              <div className="p-4">
                <DailyActivityCell 
                  activities={dayData.Activity} 
                  onItemClick={onItemClick}
                  onAddActivity={() => onMealTitleClick && onMealTitleClick(member.id, currentDay?.fullDate, 'activity')}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// ============================================
// MAIN APP COMPONENT
// ============================================
export default function FamilyHealthTracker() {
  const [viewMode, setViewMode] = useState('daily');
  const [weekOffset, setWeekOffset] = useState(0);
  const [currentDayIndex, setCurrentDayIndex] = useState(() => {
    const today = new Date().getDay();
    return today === 0 ? 6 : today - 1;
  });
  const [familyMembers, setFamilyMembers] = useState([]);
  const [weekData, setWeekData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  
  // Remember last used values for the modal
  const [lastMealType, setLastMealType] = useState('Breakfast');
  const [modalDefaults, setModalDefaults] = useState({ personId: null, date: null, mealType: null, isActivity: false });
  
  // Track selected person in weekly mobile view (persists across modal open/close)
  const [selectedWeeklyPersonIndex, setSelectedWeeklyPersonIndex] = useState(0);
  
  const weekDays = useMemo(() => {
    const today = new Date();
    today.setDate(today.getDate() + (weekOffset * 7));
    return getWeekDates(today);
  }, [weekOffset]);
  
  const weekRange = useMemo(() => formatWeekRange(weekDays), [weekDays]);
  
  const fetchData = useCallback(async () => {
    if (!weekDays.length) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const startDate = weekDays[0].fullDate;
      const endDate = weekDays[6].fullDate;
      
      const [members, foodLog, activityLog] = await Promise.all([
        airtable.getFamilyMembers(),
        airtable.getFoodLog(startDate, endDate),
        airtable.getActivityLog(startDate, endDate),
      ]);
      
      setFamilyMembers(members);
      
      const transformedData = transformDataForWeek(members, foodLog, activityLog, weekDays);
      setWeekData(transformedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [weekDays]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleAddFood = async (entry) => {
    await airtable.createFoodEntry(entry);
    await fetchData();
  };
  
  // Batch add multiple food entries
  const handleAddFoods = async (entries) => {
    await airtable.createFoodEntries(entries);
    await fetchData();
  };
  
  const handleUpdateFood = async (recordId, entry) => {
    await airtable.updateFoodEntry(recordId, entry);
    await fetchData();
  };
  
  const handleDeleteFood = async (recordId) => {
    await airtable.deleteFoodEntry(recordId);
    await fetchData();
  };
  
  const handleAddActivity = async (entry) => {
    await airtable.createActivityEntry(entry);
    await fetchData();
  };
  
  const handleUpdateActivity = async (recordId, entry) => {
    await airtable.updateActivityEntry(recordId, entry);
    await fetchData();
  };
  
  const handleDeleteActivity = async (recordId) => {
    await airtable.deleteActivityEntry(recordId);
    await fetchData();
  };
  
  const handleItemClick = (item) => {
    setEditItem(item);
    setModalDefaults({ personId: null, date: null, mealType: null });
    setShowModal(true);
  };
  
  const handleAddNew = () => {
    setEditItem(null);
    // Use current day and first family member as defaults
    const currentDay = weekDays[currentDayIndex];
    setModalDefaults({
      personId: familyMembers[0]?.id || null,
      date: currentDay?.fullDate || null,
      mealType: lastMealType,
      isActivity: false,
    });
    setShowModal(true);
  };
  
  // Handle clicking on a meal title (B, L, D, S) or Activity to add entry for that specific context
  const handleMealTitleClick = (personId, date, mealTypeOrActivity) => {
    setEditItem(null);
    if (mealTypeOrActivity === 'activity') {
      setModalDefaults({
        personId,
        date,
        mealType: null,
        isActivity: true,
      });
    } else {
      setModalDefaults({
        personId,
        date,
        mealType: mealTypeOrActivity,
        isActivity: false,
      });
    }
    setShowModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setEditItem(null);
    setModalDefaults({ personId: null, date: null, mealType: null, isActivity: false });
  };
  
  const handleMealTypeChange = (mealType) => {
    setLastMealType(mealType);
  };
  
  if (error && !isLoading) {
    return <ErrorDisplay error={error} onRetry={fetchData} />;
  }
  
  const currentDay = weekDays[currentDayIndex];
  
  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: '#FAF9F6', fontFamily: 'Georgia, "Times New Roman", serif' }}
    >
      {/* Header */}
      <header 
        className="border-b px-4 md:px-6 py-3 md:py-5"
        style={{ backgroundColor: '#FDFCFA', borderColor: '#E5E2DC' }}
      >
        <div className="max-w-6xl mx-auto">
          {/* Mobile Header */}
          <div className="md:hidden">
            {/* Row 1: Title + View Toggle */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <h1 className="text-lg font-normal tracking-tight" style={{ color: '#2C3E50' }}>
                  Health Tracker
                </h1>
                <p 
                  className="text-xs tracking-wide"
                  style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680', fontWeight: 500 }}
                >
                  {viewMode === 'daily' 
                    ? 'Daily View'
                    : 'Week at a Glance'
                  }
                </p>
              </div>
              
              {/* View Mode Toggle + Add Button */}
              <div className="flex items-center gap-2">
                <div 
                  className="flex rounded-lg overflow-hidden"
                  style={{ border: '1px solid #E5E2DC' }}
                >
                  <button
                    onClick={() => setViewMode('daily')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      viewMode === 'daily'
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-600'
                    }`}
                    style={{ fontFamily: 'system-ui, sans-serif' }}
                  >
                    Daily
                  </button>
                  <button
                    onClick={() => setViewMode('weekly')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      viewMode === 'weekly'
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-600'
                    }`}
                    style={{ fontFamily: 'system-ui, sans-serif' }}
                  >
                    Weekly
                  </button>
                </div>
                
                <button
                  onClick={handleAddNew}
                  className="w-8 h-8 rounded-full text-white flex items-center justify-center text-lg font-light hover:scale-105 transition-transform"
                  style={{ backgroundColor: '#2C3E50' }}
                  title="Add entry"
                >
                  +
                </button>
              </div>
            </div>
            
            {/* Row 2: Week Navigation - Only show for weekly view */}
            {viewMode === 'weekly' && (
              <div className="flex items-center justify-center gap-2">
                <button 
                  onClick={() => setWeekOffset(prev => prev - 1)}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                  style={{ color: '#6B6560' }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <div className="text-center flex-1">
                  <div className="text-sm font-normal" style={{ color: '#2C3E50' }}>
                    {weekRange}
                  </div>
                  {weekOffset !== 0 && (
                    <button
                      onClick={() => {
                        setWeekOffset(0);
                        const today = new Date().getDay();
                        setCurrentDayIndex(today === 0 ? 6 : today - 1);
                      }}
                      className="text-xs text-blue-500 hover:underline"
                      style={{ fontFamily: 'system-ui, sans-serif' }}
                    >
                      Today
                    </button>
                  )}
                </div>
                
                <button 
                  onClick={() => setWeekOffset(prev => prev + 1)}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                  style={{ color: '#6B6560' }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
            
            {/* Mobile Legend - Compact */}
            <div 
              className="flex items-center justify-center gap-2 mt-3 pt-3 border-t text-[10px]"
              style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560', borderColor: '#E5E2DC' }}
            >
              <span className="bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded">Processed Sugar</span>
              <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Carbs</span>
              <span className="flex items-center gap-1">
                <span style={{ color: '#4A6741' }}>●</span>
                <span>Activity</span>
              </span>
            </div>
          </div>
          
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-normal tracking-tight" style={{ color: '#2C3E50' }}>
                Ayala/Vidal Health Tracker
              </h1>
              <p 
                className="text-sm mt-1 tracking-wide"
                style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680', fontWeight: 500 }}
              >
                {viewMode === 'daily' 
                  ? 'Daily View'
                  : 'Week at a Glance'
                }
              </p>
            </div>
            
            {/* View Toggle + Navigation */}
            <div className="flex items-center gap-6">
              {/* View Mode Toggle */}
              <div 
                className="flex rounded-lg overflow-hidden"
                style={{ border: '1px solid #E5E2DC' }}
              >
                <button
                  onClick={() => setViewMode('daily')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'daily'
                      ? 'bg-gray-800 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  style={{ fontFamily: 'system-ui, sans-serif' }}
                >
                  Daily
                </button>
                <button
                  onClick={() => setViewMode('weekly')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'weekly'
                      ? 'bg-gray-800 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  style={{ fontFamily: 'system-ui, sans-serif' }}
                >
                  Weekly
                </button>
              </div>
              
              {/* Add Entry Button */}
              <button
                onClick={handleAddNew}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#2C3E50' }}
              >
                <span className="text-lg leading-none">+</span>
                <span>Add Entry</span>
              </button>
              
              {/* Week Navigation - Only show with arrows for weekly view */}
              {viewMode === 'weekly' ? (
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => setWeekOffset(prev => prev - 1)}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                    style={{ color: '#6B6560' }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <div className="text-center min-w-[200px]">
                    <div className="text-lg font-normal" style={{ color: '#2C3E50' }}>
                      {weekRange}
                    </div>
                    {weekOffset !== 0 && (
                      <button
                        onClick={() => {
                          setWeekOffset(0);
                          const today = new Date().getDay();
                          setCurrentDayIndex(today === 0 ? 6 : today - 1);
                        }}
                        className="text-xs text-blue-500 hover:underline mt-1"
                        style={{ fontFamily: 'system-ui, sans-serif' }}
                      >
                        Go to today
                      </button>
                    )}
                  </div>
                  
                  <button 
                    onClick={() => setWeekOffset(prev => prev + 1)}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                    style={{ color: '#6B6560' }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              ) : (
                /* For daily view, just show week range without arrows */
                <div className="text-center">
                  <div className="text-sm" style={{ fontFamily: 'system-ui, sans-serif', color: '#8B8680' }}>
                    Week of {weekRange}
                  </div>
                </div>
              )}
            </div>
            
            {/* Legend */}
            <div 
              className="flex items-center gap-3 text-xs"
              style={{ fontFamily: 'system-ui, sans-serif', color: '#6B6560' }}
            >
              <span className="bg-rose-100 text-rose-800 px-2 py-0.5 rounded">Processed Sugar</span>
              <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Heavy Carbs</span>
              <div className="flex items-center gap-1 ml-1">
                <span style={{ color: '#4A6741' }}>●</span>
                <span>Activity</span>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="p-4 md:p-6">
        <div className="max-w-6xl mx-auto">
          {isLoading ? (
            <LoadingSkeleton />
          ) : viewMode === 'weekly' ? (
            <WeeklyView 
              familyMembers={familyMembers} 
              weekData={weekData} 
              weekDays={weekDays}
              onItemClick={handleItemClick}
              onMealTitleClick={handleMealTitleClick}
              selectedPersonIndex={selectedWeeklyPersonIndex}
              setSelectedPersonIndex={setSelectedWeeklyPersonIndex}
            />
          ) : (
            <DailyView 
              familyMembers={familyMembers}
              weekData={weekData}
              weekDays={weekDays}
              currentDayIndex={currentDayIndex}
              setCurrentDayIndex={setCurrentDayIndex}
              onItemClick={handleItemClick}
              onMealTitleClick={handleMealTitleClick}
              onWeekChange={(delta) => setWeekOffset(prev => prev + delta)}
              onGoToToday={() => {
                setWeekOffset(0);
                const today = new Date().getDay();
                setCurrentDayIndex(today === 0 ? 6 : today - 1);
              }}
              isToday={weekOffset === 0 && currentDayIndex === (new Date().getDay() === 0 ? 6 : new Date().getDay() - 1)}
            />
          )}
        </div>
      </main>
      
      {/* Tap to edit hint */}
      {!isLoading && (
        <div className="text-center pb-4">
          <span className="text-xs text-gray-400" style={{ fontFamily: 'system-ui, sans-serif' }}>
            Tap meal labels to add • Tap items to edit
          </span>
        </div>
      )}
      
      {/* Entry Modal (Add/Edit) */}
      <EntryModal
        isOpen={showModal}
        onClose={handleCloseModal}
        familyMembers={familyMembers}
        weekDays={weekDays}
        selectedDayIndex={currentDayIndex}
        onAddFood={handleAddFood}
        onAddFoods={handleAddFoods}
        onAddActivity={handleAddActivity}
        onUpdateFood={handleUpdateFood}
        onUpdateActivity={handleUpdateActivity}
        onDeleteFood={handleDeleteFood}
        onDeleteActivity={handleDeleteActivity}
        editItem={editItem}
        defaultMealType={modalDefaults.mealType || lastMealType}
        defaultPersonId={modalDefaults.personId}
        defaultDate={modalDefaults.date}
        defaultIsActivity={modalDefaults.isActivity}
        onMealTypeChange={handleMealTypeChange}
      />
    </div>
  );
}
